import { forwardRef, type ButtonHTMLAttributes } from 'react';

import { cn } from '../lib/cn';

type Variant = 'primary' | 'secondary' | 'ghost' | 'destructive';
type Size = 'sm' | 'md' | 'lg';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
  isLoading?: boolean;
}

const VARIANTS: Record<Variant, string> = {
  primary:
    'bg-aurum-500 text-onyx-900 hover:bg-aurum-400 active:bg-aurum-600 shadow-aurum-soft',
  secondary:
    'bg-onyx-700 text-cream-50 hover:bg-onyx-500 active:bg-onyx-900 border border-aurum-500/40',
  ghost:
    'bg-transparent text-aurum-300 hover:bg-aurum-500/10 active:bg-aurum-500/20',
  destructive:
    'bg-accent-ruby text-cream-50 hover:opacity-90 active:opacity-80',
};

const SIZES: Record<Size, string> = {
  sm: 'h-9  px-3 text-sm',
  md: 'h-11 px-5 text-base',
  lg: 'h-14 px-7 text-lg',
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { className, variant = 'primary', size = 'md', isLoading, disabled, children, ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      disabled={disabled ?? isLoading}
      className={cn(
        'inline-flex items-center justify-center gap-2 rounded-aurum-md font-display tracking-wide',
        'transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-aurum-300',
        'disabled:cursor-not-allowed disabled:opacity-60',
        VARIANTS[variant],
        SIZES[size],
        className,
      )}
      {...rest}
    >
      {isLoading ? <span className="animate-pulse">…</span> : children}
    </button>
  );
});
